import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, Instagram } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { WaitlistForm } from './WaitlistForm';
import { StoreBadges } from './StoreBadges';

export const CTASection = () => {
  const { t } = useLanguage();

  return (
    <section
      id="cta"
      data-testid="cta-section"
      className="relative py-20 md:py-28 bg-[#FFFDF8]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative rounded-[2.5rem] bg-stone-900 overflow-hidden p-10 md:p-16"
        >
          {/* food image accent */}
          <div className="pointer-events-none absolute -top-20 -right-20 h-72 w-72 rounded-full overflow-hidden opacity-30">
            <img
              src="https://images.unsplash.com/photo-1747292718361-c838a9968ec7?auto=format&fit=crop&w=600&q=70"
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
          <div className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-orange-500/30 blur-3xl" />

          <div className="relative z-10 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-bold mb-5 backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-orange-400 animate-pulse" />
                {t.hero.badge}
              </span>
              <h2 className="font-display font-extrabold tracking-tight text-white text-4xl md:text-5xl lg:text-6xl text-balance">
                {t.cta.title}
              </h2>
              <p className="mt-4 text-stone-300 text-base md:text-lg max-w-xl">{t.cta.subtitle}</p>
            </div>
            <div className="md:col-span-5 flex flex-col gap-4">
              <WaitlistForm variant="dark" testIdPrefix="cta-waitlist" />
              <StoreBadges theme="dark" testIdPrefix="cta-store" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer data-testid="site-footer" className="relative bg-[#FFFDF8] border-t border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <div className="font-display font-extrabold tracking-tight text-stone-900 text-3xl md:text-4xl">
              MealMatcher
            </div>
            <p className="mt-3 text-stone-600 max-w-md">{t.footer.tag}</p>
            <span className="mt-5 inline-block font-accent text-2xl text-orange-600 -rotate-2">
              made with ♥
            </span>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-xs font-bold text-stone-900 uppercase tracking-wider mb-4">
              {t.footer.contact}
            </h4>
            <ul className="space-y-2 text-stone-600">
              <li>
                <a
                  href="mailto:corporate@mealmatcher.no"
                  data-testid="footer-email"
                  className="hover:text-orange-600 transition-colors inline-flex items-center gap-2"
                >
                  <Mail className="h-4 w-4" /> corporate@mealmatcher.no
                </a>
              </li>
              <li className="text-sm text-stone-500">Stavanger, Norway</li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="text-xs font-bold text-stone-900 uppercase tracking-wider mb-4">
              {t.footer.follow}
            </h4>
            <ul className="space-y-2 text-stone-600">
              <li>
                <a
                  href="https://www.instagram.com/mealmatcher.socials/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram"
                  className="hover:text-orange-600 transition-colors inline-flex items-center gap-2"
                >
                  <Instagram className="h-4 w-4" /> @mealmatcher.socials
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-stone-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-stone-500">
          <span>{t.footer.copyright}</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-orange-600">
              {t.footer.links.privacy}
            </Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-orange-600">
              {t.footer.links.terms}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
